/* eslint-disable react-hooks/exhaustive-deps */
import {Auth} from "types/auth"
import {useTranslation} from "react-i18next"
import {useAllEnrollmentTypes, useModel, usePaymentPeriods, useShowEnrollmentIdField} from "hooks"
import React, {useCallback, useEffect, useMemo, useState} from "react"
import {majorService, settingAcademicTrackService, termsService, userService} from "services"
import {getFieldLabel, getProgramOfTermInfo, getProgramsOfTermInfo, handleError, toastError} from "helpers"
import {Settings} from "types/settings"
import {filter, get, isEmpty, keyBy, map, omit, orderBy, uniqBy} from "lodash"
import moment from "moment/moment"
import {KlassDropdown} from "components/Select"
import styles from "uiKit/StudentInfoPanel/StudentInfoPanel.module.css"
import CampusSelect from "components/CampusSelect"
import ProgramSelect from "components/ProgramSelect"
import TermSelect from "components/TermSelect"
import {BaseDatePicker} from "components/DateTimePicker"
import {BaseInput} from "components/inputs"
import cx from "classnames"
import CircularSpin from "components/CircularSpin"
import {Icon} from "components/Icon"
import {Tooltip} from "antd"
import {getPaymentPeriodLabel} from "types/student-account/payment-period"
import PaymentPeriodSelect from "components/PaymentPeriodSelect"
import ClockStatusSelect from "components/ClockStatusSelect"
import PhaseSelect from "components/PhaseSelect"
import ShiftSelect from "components/ShiftSelect"

type EditableStudentFieldProps = {
    field:
        | "customProfileId"
        | "customUserId"
        | "diplomaDate"
        | "startDate"
        | "contractEndDate"
        | "firstDateOfClass"
        | "midpointDate"
        | "projectedEndDate"
        | "actualEndDate"
        | "orientationDate"
        | "enrollmentType"
        | "campuses"
        | "currentMajor"
        | "currentMajorVersionId"
        | "academicTrackId"
        | "startingTermId"
        | "currentPaymentPeriodId"
        | "currentTerm"
        | "initialInquiryDate"
        | "applicationCompletedDate"
        | "acceptanceDate"
        | "clockStatus"
        | "phaseIds"
        | "shiftIds"
        | "naccasGradDate"
        | "lastDayOfAttendance"
    activeProfile?: Auth.Profile
    student?: Auth.DepartmentStudent
    getUserData?: (profileId: number) => Promise<any>
    readOnly?: boolean
    onUpdateActiveProfile?: (data: any) => void
}

const EditableStudentField = ({
    field,
    activeProfile,
    student,
    getUserData,
    readOnly = false,
    onUpdateActiveProfile
}: EditableStudentFieldProps) => {
    const {t} = useTranslation(["common", "user"])
    const model = useModel()
    const {isVisible: showEnrollmentId, label: enrollmentIdLabel} = useShowEnrollmentIdField()
    const userDateFormat = model.getUserDateFormat()
    const [isEditing, setEditing] = useState(false)
    const [value, setValue] = useState<any | undefined>()
    const [relatedFields, setRelatedFields] = useState<any>({})
    const [isSubmitting, setSubmitting] = useState(false)

    const [majorVersionOptions, setMajorVersionOptions] = useState([])
    const [academicTracks, setAcademicTracks] = useState([])
    const {enrollmentTypes} = useAllEnrollmentTypes()
    const {paymentPeriods} = usePaymentPeriods()
    const [dateOptions, setDateOptions] = useState([])
    const [isShowDatePickerContractEndDate, setIsShowDatePickerContractEndDate] = useState(false)

    useEffect(() => {
        setEditing(false)
    }, [student?.profileId])

    useEffect(() => {
        ;(async function getAcademicTrackOptions() {
            if (field !== "academicTrackId" || !activeProfile?.startingTerm?.id) return
            if (!activeProfile) return
            try {
                const [{data}, term] = await Promise.all([
                    settingAcademicTrackService.getAllAcademicTracks({
                        sort: {orderBy: "name", orderDir: "asc"}
                    }),
                    termsService.getTerm(activeProfile.startingTerm.id)
                ])
                const academicTracksKeyById = keyBy(data, "trackId")
                const tracks = term.academicTrackIds
                    .map((academicTrackId) => {
                        const track = academicTracksKeyById[academicTrackId]
                        if (track) {
                            return {trackId: track.trackId, name: track.name}
                        }
                        return null
                    })
                    .filter(Boolean)

                setAcademicTracks(tracks)
            } catch (error) {
                console.error(error)
            }
        })()
    }, [field, activeProfile?.startingTerm?.id])

    useEffect(() => {
        ;(async function getDateOptions() {
            if (!["startDate", "contractEndDate"].includes(field)) {
                setDateOptions([])
                return
            }
            if (!activeProfile?.startingTerm?.id) {
                setDateOptions(
                    activeProfile?.[field]
                        ? [
                              {
                                  id: moment.utc(activeProfile?.[field]).toISOString(),
                                  name: moment.utc(activeProfile?.[field]).format(userDateFormat)
                              }
                          ]
                        : []
                )
                return
            }
            try {
                const {data: programsOfTerm} = await majorService.getMajorByTerm([activeProfile.startingTerm.id])
                const programsByTerm = getProgramsOfTermInfo(programsOfTerm, activeProfile)
                const mappedField = field === "startDate" ? "startDate" : "projectedEndDate"
                const options = programsByTerm
                    .map((item) => {
                        return item[mappedField]
                            ? {
                                  id: moment.utc(item[mappedField]).toISOString(),
                                  name: moment.utc(item[mappedField]).format(userDateFormat),
                                  contractEndDate: item.projectedEndDate
                                      ? moment.utc(item.projectedEndDate).toISOString()
                                      : null,
                                  projectedEndDate: item.projectedEndDate
                                      ? moment.utc(item.projectedEndDate).toISOString()
                                      : null,
                                  orientationDate: item.orientationDate
                                      ? moment.utc(item.orientationDate).toISOString()
                                      : null
                              }
                            : null
                    })
                    .filter(Boolean)
                setDateOptions(options)
            } catch (error) {
                console.error(error)
                setDateOptions([])
            }
        })()
    }, [field, activeProfile?.startingTerm?.id, activeProfile?.academicTrackId])

    useEffect(() => {
        ;(async function getMajorVersionOptions() {
            if (field !== "currentMajorVersionId" || !activeProfile?.currentMajor) {
                setMajorVersionOptions([])
                return
            }
            try {
                const majorVersions = await majorService.getMajorVersions({
                    orderBy: "version_name",
                    orderDir: "ASC",
                    program_id: (activeProfile.currentMajor as Auth.CurrentMajor).programId,
                    isActive: true
                })
                setMajorVersionOptions(
                    majorVersions.map((item) => ({
                        ...item,
                        versionName: item.version_name,
                        versionCode: item.version_code
                    }))
                )
            } catch (error) {
                console.error(error)
                setMajorVersionOptions([])
            }
        })()
    }, [field, activeProfile?.currentMajor])

    const label: string = useMemo(() => {
        switch (field) {
            case "customProfileId": {
                const isProspectOrApplicant = [
                    Auth.UserProfileState.Applicant,
                    Auth.UserProfileState.Prospect
                ].includes(activeProfile?.state)
                const isStudentOrAlumni = [Auth.UserProfileState.Student, Auth.UserProfileState.Alumni].includes(
                    activeProfile?.state
                )
                return isProspectOrApplicant
                    ? t("studentInfo.applicantID")
                    : isStudentOrAlumni
                    ? enrollmentIdLabel
                    : t("studentInfo.profileId")
            }
            case "customUserId":
                return t("studentInfo.userID")
            case "diplomaDate":
                return t("studentInfo.highschoolGradDate")
            case "startDate":
                return getFieldLabel(model, Settings.GeneralLabel.StartDate, t("studentInfo.startDate"))
            case "contractEndDate":
                return getFieldLabel(model, Settings.GeneralLabel.ContractEndDate, t("studentInfo.contractEndDate"))
            case "firstDateOfClass":
                return getFieldLabel(model, Settings.GeneralLabel.FirstDateOfClass, t("studentInfo.firstDateOfClass"))
            case "midpointDate":
                return getFieldLabel(model, Settings.GeneralLabel.MidpointDate, t("studentInfo.midpointDate"))
            case "projectedEndDate":
                return getFieldLabel(model, Settings.GeneralLabel.ProjectedEndDate, t("studentInfo.projectedEndDate"))
            case "naccasGradDate":
                return getFieldLabel(model, Settings.GeneralLabel.NaccasGradDate, t("studentInfo.naccasGradDate"))
            case "actualEndDate":
                return (
                    getFieldLabel(model, Settings.GeneralLabel.ActualEndDate, t("studentInfo.actualEndDate")) +
                    " (System generated)"
                )
            case "orientationDate":
                return t("studentInfo.orientationDate")
            case "lastDayOfAttendance":
                return "Last Day of Attendance"
            case "enrollmentType":
                return t("studentInfo.enrollmentType")
            case "campuses":
                return getFieldLabel(model, Settings.GeneralLabel.Campus, t("studentInfo.campus"))
            case "currentMajor":
                return getFieldLabel(model, Settings.GeneralLabel.Program, t("studentInfo.program"))
            case "currentMajorVersionId":
                return getFieldLabel(model, Settings.GeneralLabel.ProgramVersion, t("studentInfo.programVersion"))
            case "academicTrackId":
                return getFieldLabel(model, Settings.GeneralLabel.AcademicTrack, t("studentInfo.academicTrack"))
            case "startingTermId":
                return t("studentInfo.startingTerm")
            case "currentPaymentPeriodId":
                return t("studentInfo.currentPaymentPeriod")
            case "currentTerm":
                return getFieldLabel(model, Settings.GeneralLabel.CurrentTerm, t("studentInfo.currentTerm"))
            case "initialInquiryDate":
                return "Inquiry date"
            case "applicationCompletedDate":
                return "App Completion date"
            case "acceptanceDate":
                return "Admission acceptance"
            case "clockStatus":
                return "Clock Status"
            case "phaseIds":
                return "Phases"
            case "shiftIds":
                return "Shifts"
            default:
                return ""
        }
    }, [field, activeProfile, student, enrollmentIdLabel])

    const displayValue: React.ReactNode = useMemo(() => {
        switch (field) {
            case "customProfileId":
                return activeProfile?.customProfileId
            case "customUserId":
                return get(student, "customUserId")
            case "diplomaDate": {
                const date = student?.diplomaDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "startDate": {
                const date = activeProfile?.startDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "contractEndDate": {
                const date = activeProfile?.contractEndDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "firstDateOfClass": {
                const date = activeProfile?.firstDateOfClass
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "midpointDate": {
                const date = activeProfile?.midpointDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "projectedEndDate": {
                const date = activeProfile?.projectedEndDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "naccasGradDate": {
                const date = activeProfile?.naccasGradDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "actualEndDate": {
                const date = activeProfile?.actualEndDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "orientationDate": {
                const date = activeProfile?.orientationDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "enrollmentType":
                const enrollmentType = (enrollmentTypes || []).find(({code}) => code === activeProfile?.enrollmentType)
                return enrollmentType ? enrollmentType.name : "-"
            case "campuses": {
                if (!activeProfile?.campuses?.length) return "-"
                return (
                    <>
                        {activeProfile.campuses
                            .slice(0, 5)
                            .map((campus) => campus.name)
                            .join(", ")}
                        {activeProfile.campuses.length > 5 && (
                            <span style={{color: "var(--primary-400-base)"}}>
                                &nbsp;(+{activeProfile.campuses.length - 5})
                            </span>
                        )}
                    </>
                )
            }
            case "currentMajor":
                return get(activeProfile, "currentMajor.name", "-")
            case "currentMajorVersionId":
                const versionName = get(activeProfile, "currentMajorVersionId.versionName", "-")
                const versionCode = get(activeProfile, "currentMajorVersionId.versionCode", "")
                return `${versionName}${versionCode ? ` (${versionCode})` : ""}`
            case "academicTrackId":
                return activeProfile?.academicTrack || "-"
            case "startingTermId":
                return get(activeProfile, "startingTerm.name", "-")
            case "currentPaymentPeriodId":
                return activeProfile?.currentPaymentPeriodId
                    ? getPaymentPeriodLabel(
                          paymentPeriods.find((period) => period.periodId === activeProfile?.currentPaymentPeriodId),
                          userDateFormat
                      )
                    : "-"
            case "currentTerm":
                return get(activeProfile, "currentTerm.name", "-")
            case "initialInquiryDate": {
                const date = student?.initialInquiryDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "applicationCompletedDate": {
                const date = student?.applicationCompletedDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "acceptanceDate": {
                const date = student?.acceptanceDate
                return date ? moment.utc(date).format(userDateFormat) : "-"
            }
            case "lastDayOfAttendance":
                return student?.lastDayOfAttendance
                    ? moment.utc(student?.lastDayOfAttendance).format(userDateFormat)
                    : "-"
            case "clockStatus":
                return activeProfile?.clockStatus || "-"
            case "phaseIds":
                return !isEmpty(activeProfile?.phases)
                    ? map(
                          orderBy(uniqBy(activeProfile?.phases, "phaseId"), "type", "desc"),
                          (phase) => phase.name
                      ).join(", ")
                    : "-"
            case "shiftIds":
                return !isEmpty(activeProfile?.shifts)
                    ? map(
                          orderBy(uniqBy(activeProfile?.shifts, "shiftId"), "type", "desc"),
                          (shift) => shift.name
                      ).join(", ")
                    : "-"
            default:
                return null
        }
    }, [field, activeProfile, student])

    const handleEditClick = useCallback(() => {
        switch (field) {
            case "customUserId":
            case "initialInquiryDate":
            case "applicationCompletedDate":
            case "acceptanceDate":
            case "diplomaDate":
                setValue(student?.[field])
                break
            case "customProfileId":
            case "firstDateOfClass":
            case "midpointDate":
            case "projectedEndDate":
            case "orientationDate":
            case "currentMajor":
            case "currentMajorVersionId":
            case "currentTerm":
            case "startDate":
            case "contractEndDate":
            case "lastDayOfAttendance":
            case "campuses":
                setValue(activeProfile?.[field])
                if (field === "contractEndDate") {
                    const isShowDatePicker = !dateOptions.some((item) => item.id === activeProfile?.[field])
                    setIsShowDatePickerContractEndDate(isShowDatePicker)
                }
                break
            case "enrollmentType":
                const enrollmentType = (enrollmentTypes || []).find(({code}) => code === activeProfile?.enrollmentType)
                setValue(enrollmentType)
                break
            case "academicTrackId":
                setValue(
                    activeProfile?.academicTrackId
                        ? {trackId: activeProfile?.academicTrackId, name: activeProfile?.academicTrack}
                        : undefined
                )
                break
            case "startingTermId":
                setValue(activeProfile?.startingTerm)
                break
            case "currentPaymentPeriodId":
                setValue(
                    activeProfile?.currentPaymentPeriodId &&
                        paymentPeriods.find((period) => period.periodId === activeProfile?.currentPaymentPeriodId)
                )
                break
            case "clockStatus":
                setValue(activeProfile?.clockStatus)
                break
            case "phaseIds":
                setValue(orderBy(activeProfile?.phases, "type", "desc"))
                break
            case "shiftIds":
                setValue(orderBy(activeProfile?.shifts, "type", "desc"))
                break
            default:
                setValue(undefined)
        }
        setEditing(true)
        setRelatedFields({})
    }, [field, activeProfile, student, dateOptions])

    const onChangeStartingTerm = async (value) => {
        const relatedFields = {
            startDate: null,
            contractEndDate: null,
            projectedEndDate: null,
            orientationDate: null
        }
        if (value.id) {
            const {data: programsOfTerms} = await majorService.getMajorByTerm([value.id])
            const programByTerm = getProgramOfTermInfo(programsOfTerms, student)
            if (programByTerm?.startDate) {
                relatedFields.startDate = moment.utc(programByTerm.startDate).toISOString()
            }
            if (programByTerm?.projectedEndDate) {
                relatedFields.contractEndDate = moment.utc(programByTerm.projectedEndDate).toISOString()
                relatedFields.projectedEndDate = moment.utc(programByTerm.projectedEndDate).toISOString()
            }
            if (programByTerm?.orientationDate) {
                relatedFields.orientationDate = moment.utc(programByTerm.orientationDate).toISOString()
            }
        }
        onUpdateActiveProfile?.(relatedFields)
        setRelatedFields(relatedFields)
    }

    const renderInput = () => {
        const dropdownComponents = {
            ClearIndicator: () => <div className="w-20" />,
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null
        }
        const dropdownStyleCustom = {
            menuPortal: (styles) => ({
                ...styles,
                zIndex: 9999
            }),
            menu: (styles) => ({
                ...styles,
                width: "max-content",
                minWidth: "100%",
                maxWidth: "450px",
                right: 0
            })
        }
        const campusIds = map(activeProfile?.campuses ?? [], (item) => item.id)

        switch (field) {
            case "enrollmentType":
                return (
                    <KlassDropdown
                        isClearable
                        options={enrollmentTypes || []}
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "campuses":
                return (
                    <CampusSelect
                        isClearable
                        isMulti
                        value={value ?? []}
                        onChange={(newValue) => {
                            if (newValue?.length && newValue?.length > 1) {
                                toastError("You can only choose one campus for the profile")
                            } else {
                                setValue(newValue)
                                const relatedFields = {
                                    currentMajor: null,
                                    currentMajorVersionId: null,
                                    startingTerm: null,
                                    currentTerm: null,
                                    academicTrack: null,
                                    academicTrackId: null,
                                    startDate: null,
                                    contractEndDate: null,
                                    projectedEndDate: null,
                                    orientationDate: null
                                }
                                setRelatedFields(relatedFields)
                                onUpdateActiveProfile?.(relatedFields)
                            }
                        }}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "currentMajor":
                return (
                    <ProgramSelect
                        isClearable
                        isActive
                        value={value}
                        onChange={(value) => {
                            setValue(value)
                            const relatedFields = {
                                currentMajorVersionId: null,
                                startingTermId: null,
                                startDate: null,
                                contractEndDate: null,
                                projectedEndDate: null,
                                orientationDate: null
                            }
                            setRelatedFields(relatedFields)
                            onUpdateActiveProfile?.(relatedFields)
                        }}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        campusId={campusIds}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "currentMajorVersionId":
                return (
                    <KlassDropdown
                        isClearable
                        options={majorVersionOptions}
                        getOptionLabel={({versionCode, versionName}) =>
                            versionCode ? ` ${versionName} (${versionCode})` : versionName
                        }
                        getOptionValue={({versionCode, versionName}) =>
                            versionCode ? ` ${versionName} (${versionCode})` : versionName
                        }
                        value={value}
                        onChange={(value) => {
                            setValue(value)
                            const relatedFields = {
                                startingTermId: null,
                                startDate: null,
                                contractEndDate: null,
                                projectedEndDate: null,
                                orientationDate: null
                            }
                            setRelatedFields(relatedFields)
                            onUpdateActiveProfile?.(relatedFields)
                        }}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "academicTrackId":
                return (
                    <KlassDropdown
                        isClearable
                        options={academicTracks}
                        valueKey="trackId"
                        labelKey="name"
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "startingTermId":
                return (
                    <TermSelect
                        isClearable
                        value={value}
                        onChange={(newValue) => {
                            setValue(newValue)
                            onChangeStartingTerm(newValue)
                        }}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                        campusIds={campusIds}
                        majorId={get(activeProfile, "currentMajor.id")}
                        majorVersionId={get(activeProfile, "currentMajorVersionId.id")}
                    />
                )
            case "currentTerm":
                return (
                    <TermSelect
                        isClearable
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                        campusIds={campusIds}
                    />
                )
            case "currentPaymentPeriodId":
                return (
                    <PaymentPeriodSelect
                        isClearable
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "startDate":
                return (
                    <KlassDropdown
                        isClearable
                        options={dateOptions}
                        value={value ? dateOptions.find((item) => item.id === value) : undefined}
                        onChange={(value) => {
                            setValue(value?.id)
                            const relatedFields = {
                                contractEndDate: value.contractEndDate
                                    ? moment.utc(value.contractEndDate).toISOString()
                                    : null,
                                projectedEndDate: value.projectedEndDate
                                    ? moment.utc(value.projectedEndDate).toISOString()
                                    : null,
                                orientationDate: value.orientationDate
                                    ? moment.utc(value.orientationDate).toISOString()
                                    : null
                            }
                            setRelatedFields(relatedFields)
                            onUpdateActiveProfile?.(relatedFields)
                        }}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "contractEndDate":
                return (
                    <div className={styles.contractEndDateWrap}>
                        {isShowDatePickerContractEndDate ? (
                            <BaseDatePicker
                                value={value ? moment.utc(value) : undefined}
                                isUtcDate
                                onChange={(value) => setValue(value?.toISOString())}
                                className={styles.contentInput}
                                suffixIcon={null}
                                clearIcon={null}
                            />
                        ) : (
                            <KlassDropdown
                                isClearable
                                options={dateOptions}
                                value={value ? dateOptions.find((item) => item.id === value) : undefined}
                                onChange={(value) => setValue(value?.id)}
                                className={styles.contentInput}
                                menuPortalTarget={document.body}
                                components={dropdownComponents}
                                stylesCustom={dropdownStyleCustom}
                                readOnly={readOnly}
                            />
                        )}
                        <div
                            className={styles.extraIconWrap}
                            onClick={() => setIsShowDatePickerContractEndDate(!isShowDatePickerContractEndDate)}>
                            <Icon
                                icon={isShowDatePickerContractEndDate ? "CALENDAR_INPUT" : "ARROW_DOWN"}
                                className={styles.extraIcon}
                            />
                        </div>
                    </div>
                )
            case "firstDateOfClass":
            case "midpointDate":
            case "projectedEndDate":
            case "actualEndDate":
            case "initialInquiryDate":
            case "applicationCompletedDate":
            case "acceptanceDate":
            case "orientationDate":
            case "diplomaDate":
            case "lastDayOfAttendance":
                return (
                    <BaseDatePicker
                        value={value ? moment.utc(value) : undefined}
                        isUtcDate
                        onChange={(value) => setValue(value?.toISOString())}
                        className={styles.contentInput}
                        suffixIcon={null}
                        clearIcon={null}
                    />
                )
            case "clockStatus":
                return (
                    <ClockStatusSelect
                        isClearable
                        value={value}
                        onChange={setValue}
                        className={styles.contentInput}
                        menuPortalTarget={document.body}
                        components={dropdownComponents}
                        stylesCustom={dropdownStyleCustom}
                    />
                )
            case "phaseIds": {
                const groupPhaseIds = activeProfile?.groupPhases.map((item) => item.phaseId) ?? []
                return (
                    <PhaseSelect
                        isClearable
                        value={value}
                        isMulti
                        onChange={(newValue) => {
                            setValue(newValue)
                        }}
                        components={dropdownComponents}
                        stylesCustom={{
                            ...dropdownStyleCustom,
                            multiValue: (styles, state) => {
                                return {
                                    ...styles,
                                    backgroundColor: groupPhaseIds.includes(state.data.phaseId) ? "#262e37" : "#1e90ff"
                                }
                            }
                        }}
                    />
                )
            }
            case "shiftIds": {
                const groupShiftIds = activeProfile?.groupShifts.map((item) => item.shiftId) ?? []
                return (
                    <ShiftSelect
                        isClearable
                        value={value}
                        isMulti
                        campusIds={campusIds}
                        onChange={(newValue) => {
                            setValue(newValue)
                        }}
                        components={dropdownComponents}
                        stylesCustom={{
                            ...dropdownStyleCustom,
                            multiValue: (styles, state) => {
                                return {
                                    ...styles,
                                    backgroundColor: groupShiftIds.includes(state.data.shiftId) ? "#262e37" : "#1e90ff"
                                }
                            }
                        }}
                    />
                )
            }
            case "customProfileId":
            case "customUserId":
            default:
                return <BaseInput value={value} onChange={setValue} className={styles.contentInput} />
        }
    }

    const handleSaveClick = async () => {
        if (!student?.profileId && !activeProfile?.id) return
        try {
            setSubmitting(true)
            let submitData = {
                [field]: value ?? null,
                ...omit(relatedFields, ["startingTerm", "academicTrack"])
            }
            switch (field) {
                case "enrollmentType":
                    submitData[field] = value?.id || null
                    break
                case "campuses":
                    submitData[field] = value?.map((campus) => ({id: campus.id, name: campus.name})) ?? []
                    break
                case "currentMajor":
                    submitData[field] = value?.id || null
                    break
                case "currentMajorVersionId":
                    submitData[field] = value?.id || null
                    break
                case "academicTrackId":
                    submitData[field] = value?.trackId || null
                    break
                case "startingTermId": {
                    submitData[field] = value?.id || null
                    break
                }
                case "currentTerm":
                    submitData[field] = value?.id || null
                    break
                case "currentPaymentPeriodId":
                    submitData[field] = value?.periodId || null
                    break
                case "clockStatus":
                    submitData[field] = value?.id || null
                    break
                case "phaseIds": {
                    const groupPhaseIds = map(activeProfile?.groupPhases ?? [], (phase) => phase.phaseId)
                    submitData[field] = map(value, (item) => item.phaseId).filter(
                        (item) => !groupPhaseIds.includes(item)
                    )
                    const groupPhaseIdsData = filter(value, (item) => item.type === "group").map((item) => item.phaseId)
                    submitData["groupPhaseIds"] = groupPhaseIdsData
                    break
                }
                case "shiftIds": {
                    const groupShiftIds = map(activeProfile?.groupShifts ?? [], (shift) => shift.shiftId)
                    submitData[field] = map(value, (item) => item.shiftId).filter(
                        (item) => !groupShiftIds.includes(item)
                    )
                    const groupShiftIdsData = filter(value, (item) => item.type === "group").map((item) => item.shiftId)
                    submitData["groupShiftIds"] = groupShiftIdsData
                    break
                }
            }
            if (["customUserId", "diplomaDate"].includes(field)) {
                await userService.updateUserById(student?.userId || activeProfile?.userId, submitData)
            } else {
                await userService.updateProfileUserById(student?.profileId || activeProfile?.id, {
                    type: student?.type || activeProfile?.type,
                    ...submitData
                })
            }
            await getUserData?.(student?.profileId || activeProfile?.id)
            setEditing(false)
            setValue(undefined)
            setRelatedFields({})
        } catch (error) {
            handleError(error)
        } finally {
            setSubmitting(false)
        }
    }

    const editable = !["actualEndDate", "naccasGradDate"].includes(field) && !readOnly

    if (field === "customProfileId" && !showEnrollmentId) {
        const isStudentOrAlumni = [Auth.UserProfileState.Student, Auth.UserProfileState.Alumni].includes(
            activeProfile?.state
        )
        if (isStudentOrAlumni) {
            return null
        }
    }

    return (
        <div className={styles.contentWrap}>
            <div
                className={cx(styles.contentTitle, {[styles.editable]: editable})}
                onClick={editable ? handleEditClick : undefined}
                title={label}>
                {label}
            </div>
            {isEditing ? (
                <div className={styles.contentValueWrap}>
                    <div className={cx(styles.contentValue)}>{renderInput()}</div>
                    <div className={styles.contentActions}>
                        {isSubmitting ? (
                            <CircularSpin size="small" />
                        ) : (
                            <div className={styles.contentAction} onClick={handleSaveClick}>
                                <Icon icon="CHECK_CIRCLE" />
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <Tooltip title={displayValue} placement="topRight">
                    <div
                        className={cx(styles.contentValue, {[styles.editable]: editable})}
                        onClick={editable ? handleEditClick : undefined}>
                        <span className={styles.displayValue}>{displayValue}</span>
                    </div>
                </Tooltip>
            )}
        </div>
    )
}

export default EditableStudentField
